import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getV2Embed, getV3Embed, getV4Embed } from '@opengov/reporting-embeds';
import { ICurrentOrLegacyReport } from '@opengov/reporting-types';
import {
  getCurrentOrLegacyReportsByEntityId,
  getCurrentOrLegacySavedViews,
  ICurrentOrLegacySavedView
} from '@opengov/reporting-legacy-module';
import { IEntity } from '@opengov/og-api-ts';
import { useFeatureFlagStore } from '@opengov/feature-flag/launchDarkly';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import {
  GALLERY,
  FLAG_PRODUCT_REPORTING_FEATURE_ACCOUNT_SUMMARY_CHARTS_IN_EMBEDS,
  FLAG_PRODUCT_REPORTING_CHART_AND_DETAILS_V3_ENABLED,
  FLAG_BNP_NEW_REPORT_TILE_MODAL,
  FLAG_PRODUCT_REPORTING_FEATURE_CHARTS_CUSTOMIZATION_TITLE_AND_DESCRIPTION
} from '@opengov/stories-shared/constants';

import { ICurrentOrLegacySavedViewInfo, IEmbed, IModalMessages, ISettings, ISessionUser, IStoryEmbedsResponse } from '../../../types';
import { buildVisualizationSelectData, CHART_TYPES } from '../../../stories/utils/visualizationSelect';
import { usePublicationById } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationById';
import CircularProgress from '@mui/material/CircularProgress';
import { storyViewQueryKeys } from '../../../stories/components/StoryView/hooks/storyViewQueryKeys';
import { useUpdateStoryContentDB } from '../../../stories/components/StoryView/hooks';
import { useDeleteReportEmbeds } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationReportEmbedMutations';
import { useToast } from '../../../stories/components/Toast/context/useToast.context';
import { transport } from '@opengov/app-core/transport';
import {
  getStoryContentsById,
  getTemplateContentsById,
  processStoryContentOrTemplateContent
} from '@opengov/stories-shared/graphQL';
import { orderStoryContents, sortStoryContents } from '@opengov/stories-shared/utils';
import { deleteStoryEmbed as deleteStoryEmbedQuery } from '../../../stories/graphQL/queries';
import { insertEmbeds, insertEmbedsWithEmbedData, removeEmbeds } from '../../../stories/routes/hasura';
import { postQuery } from '../../../stories/effects/publications/postQuery';
import { TAG_SCRIPT } from '../../../stories/constants';
import { AddTile } from './AddTile';
import { embedPickerQueryKeys } from './hooks/embedPickerQueryKeys';
import { useCreateEmbed } from './hooks/useCreateEmbed';
import { useCreateV3Embed } from './hooks/useCreateV3Embed';
import { useCreateV4Embed } from './hooks/useCreateV4Embed';
import { usePreviewEmbed } from './hooks/usePreviewEmbed';
import { UseClonedEmbedParams, useClonedEmbed } from './hooks/useClonedEmbed';
import { UseEmbedParams, useEmbed } from './hooks/useEmbed';
import { UseReportsForEntityParams, useReportsForEntity } from './hooks/useReportsForEntity';
import { UseSavedViewInfoParams, useSavedViewInfo } from './hooks/useSavedViewInfo';
import { UseSavedViewsForReportParams, useSavedViewsForReport } from './hooks/useSavedViewsForReport';
import { useEmbedSession } from './hooks/useEmbedSession';

type ContainerProps = {
  isTemplate: boolean;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContents: IStoryContent[];
  settings?: ISettings | null;
  messages: IModalMessages;
  storyContent: IStoryContent;
  selectedEmbed: IStoryEmbedsResponse | null;
  isReportEmbedListSelected: boolean;
  onClose(): void;
  publicationId?: string;
  isAdmin: boolean;
  entity?: IEntity;
  user?: ISessionUser;
  useNewAddTileUI?: boolean;
};

const EMPTY_EMBED = { data: null } as IEmbed;
const INITIAL_SAVED_VIEW_INFO = {
  defaultType: null,
  visualizations: null,
  hasAnnualDataOnly: true,
  fiscalYears: null
} as unknown as ICurrentOrLegacySavedViewInfo;
const noop = () => { };

function useAddTileState() {
  const queryClient = useQueryClient();

  const [embed, setEmbed] = useState<IEmbed>(EMPTY_EMBED);
  const [hasEmbedError, setHasEmbedError] = useState(false);
  const [isFetchingEmbed, setIsFetchingEmbed] = useState(false);
  const [groupDataBy, setGroupDataBy] = useState('category');
  const [selectedGroupings, setSelectedGroupings] = useState<string[]>([]);

  const [reportsParams, setReportsParams] = useState<UseReportsForEntityParams>(null);
  const reportsQuery = useReportsForEntity(reportsParams);

  const [savedViewsParams, setSavedViewsParams] = useState<UseSavedViewsForReportParams>(null);
  const savedViewsQuery = useSavedViewsForReport(savedViewsParams);

  const [savedViewInfoParams, setSavedViewInfoParams] = useState<UseSavedViewInfoParams>(null);
  const savedViewInfoQuery = useSavedViewInfo(savedViewInfoParams);
  const savedViewInfo = savedViewInfoQuery.data ?? INITIAL_SAVED_VIEW_INFO;

  const visualizationSelectOptions = useMemo(
    () => buildVisualizationSelectData(savedViewInfo?.defaultType, savedViewInfo?.visualizations ?? []),
    [savedViewInfo]
  );

  const createEmbedMutation = useCreateEmbed();
  const createV3EmbedMutation = useCreateV3Embed();
  const createV4EmbedMutation = useCreateV4Embed();
  const previewEmbedMutation = usePreviewEmbed();

  const isCreatingEmbed =
    createEmbedMutation.isLoading ||
    createV3EmbedMutation.isLoading ||
    createV4EmbedMutation.isLoading ||
    previewEmbedMutation.isLoading;

  const onEmbedChange = useCallback((newEmbed: any) => {
    setEmbed(newEmbed);
    setHasEmbedError(false);
  }, []);

  const onEmbedError = useCallback(() => setHasEmbedError(true), []);

  const resetEmbed = useCallback(() => {
    setEmbed(EMPTY_EMBED);
    setHasEmbedError(false);
  }, []);

  const getReports = useCallback(
    (params: {
      entityId: number;
      includeAccountSummaryReports?: boolean;
      includeMultiDatasetReports?: boolean;
    }): Promise<ICurrentOrLegacyReport[]> => {
      setReportsParams(params);
      return queryClient.fetchQuery({
        queryKey: embedPickerQueryKeys.reports(params),
        queryFn: async () => {
          const result = await getCurrentOrLegacyReportsByEntityId({
            entityId: params.entityId,
            includeAccountSummaryReports: params.includeAccountSummaryReports,
            includeMultiDatasetReports: params.includeMultiDatasetReports
          });
          return result as ICurrentOrLegacyReport[];
        },
        staleTime: 5 * 60 * 1000
      });
    },
    [queryClient]
  );
  const resetReports = useCallback(() => setReportsParams(null), []);

  const getSavedViews = useCallback(
    (params: {
      reportId?: string;
      legacyReportId?: string;
      reportVersion: string;
      entity: any;
    }): Promise<ICurrentOrLegacySavedView[]> => {
      setSavedViewsParams(params);
      return queryClient.fetchQuery({
        queryKey: embedPickerQueryKeys.savedViews({
          reportId: params.reportId,
          legacyReportId: params.legacyReportId,
          reportVersion: params.reportVersion
        }),
        queryFn: async () => {
          const result = await getCurrentOrLegacySavedViews({
            reportId: params.reportId,
            legacyReportId: params.legacyReportId,
            reportVersion: params.reportVersion,
            entity: params.entity
          });
          return (result as ICurrentOrLegacySavedView[]) ?? [];
        },
        staleTime: 5 * 60 * 1000
      });
    },
    [queryClient]
  );
  const resetSavedViews = useCallback(() => setSavedViewsParams(null), []);

  const getSavedViewInfo = useCallback(({ savedViewId }: { savedViewId: string }) => {
    setSavedViewInfoParams(savedViewId ? { savedViewId } : null);
  }, []);
  const resetSavedViewInfo = useCallback(() => setSavedViewInfoParams(null), []);

  const resetGroupDataBy = useCallback(() => setGroupDataBy('category'), []);
  const resetSelectedGroupings = useCallback(() => setSelectedGroupings([]), []);

  const [embedParams, setEmbedParams] = useState<UseEmbedParams>(null);
  useEmbed(embedParams);

  const getEmbed = useCallback(
    async ({ id }: { id: string }): Promise<IEmbed> => {
      setEmbedParams({ embedId: id });
      setIsFetchingEmbed(true);
      try {
        const result = await queryClient.fetchQuery({
          queryKey: embedPickerQueryKeys.embed({ embedId: id }),
          queryFn: () => getV2Embed(id)
        });
        setEmbed(result as unknown as IEmbed);
        return result as unknown as IEmbed;
      } finally {
        setIsFetchingEmbed(false);
      }
    },
    [queryClient]
  );

  const [clonedEmbedParams, setClonedEmbedParams] = useState<UseClonedEmbedParams>(null);
  useClonedEmbed(clonedEmbedParams);

  const cloneEmbedById = useCallback(
    async ({ id, embedVersion }: { id: string; embedVersion: string }) => {
      const version = (embedVersion as 'v2' | 'v3' | 'v4') ?? 'v2';
      setClonedEmbedParams({ embedId: id, embedVersion: version });
      setIsFetchingEmbed(true);
      try {
        type FetchResult = { embed: any; groupDataBy: string; tableColumns: string[] };
        const { embed: fetched, groupDataBy: gdBy, tableColumns } =
          await queryClient.fetchQuery<FetchResult>({
            queryKey: [...embedPickerQueryKeys.embed({ embedId: id }), 'cloned', version],
            queryFn: async (): Promise<FetchResult> => {
              if (version === 'v4') {
                return { embed: await getV4Embed(id), groupDataBy: 'category', tableColumns: [] };
              }
              if (version === 'v3') {
                return { embed: await getV3Embed(id), groupDataBy: 'category', tableColumns: [] };
              }
              const raw = await getV2Embed(id);
              let gdb = 'category';
              let tCols: string[] = [];
              const componentProps = (raw as any)?.data?.sdk?.componentProps;
              if (componentProps?.group_data_by && componentProps?.table_columns) {
                gdb = componentProps.group_data_by;
                tCols = componentProps.table_columns;
              }
              return { embed: raw, groupDataBy: gdb, tableColumns: tCols };
            }
          });

        let previewed: any;
        if (version === 'v2') {
          previewed = await previewEmbedMutation.mutateAsync({
            version: fetched.version,
            title: fetched.title,
            description: fetched.description,
            report_id: fetched.reportId,
            saved_view_id: fetched.savedViewId,
            report_version: fetched.report_version,
            type: CHART_TYPES[fetched.type] || fetched.type,
            group_data_by: gdBy,
            table_columns: tableColumns,
            id
          } as any);
        } else {
          previewed = await previewEmbedMutation.mutateAsync({ ...fetched, shouldSync: false, id } as any);
        }

        setEmbed(previewed as unknown as IEmbed);
        setGroupDataBy(gdBy);
        setSelectedGroupings(tableColumns);
        return previewed;
      } finally {
        setIsFetchingEmbed(false);
      }
    },
    [queryClient, previewEmbedMutation]
  );

  const previewEmbed = useCallback(
    async (props: any) => previewEmbedMutation.mutateAsync(props),
    [previewEmbedMutation]
  );

  const createEmbed = useCallback(
    async (props: any): Promise<IEmbed> => {
      const result = await createEmbedMutation.mutateAsync(props);
      // Intentionally NOT calling setEmbed — createEmbed is only called from the
      // Add handler which uses the return value directly and then closes the modal.
      // Calling setEmbed here triggers componentDidUpdate → generatePreview with
      // incomplete embed data (no html/css), causing a TypeError in buildTile.
      return result as unknown as IEmbed;
    },
    [createEmbedMutation]
  );

  const createV3Embed = useCallback(
    async (props: any): Promise<any> => {
      const result = await createV3EmbedMutation.mutateAsync(props);
      return result;
    },
    [createV3EmbedMutation]
  );

  const createV4Embed = useCallback(
    async (props: any): Promise<any> => {
      const result = await createV4EmbedMutation.mutateAsync(props);
      return result;
    },
    [createV4EmbedMutation]
  );

  return {
    embed,
    hasEmbedError,
    isCreatingEmbed,
    isFetchingEmbed,
    groupDataBy,
    selectedGroupings,
    setGroupDataBy,
    setSelectedGroupings,
    reports: reportsQuery.data ?? [],
    isRequestingReports: reportsQuery.isFetching,
    savedViews: savedViewsQuery.data ?? [],
    isRequestingSavedViews: savedViewsQuery.isFetching,
    savedViewInfo,
    isRequestingSavedViewInfo: savedViewInfoQuery.isFetching,
    visualizationSelectOptions,
    onEmbedChange,
    onEmbedError,
    resetEmbed,
    getReports,
    resetReports,
    getSavedViews,
    resetSavedViews,
    getSavedViewInfo,
    resetSavedViewInfo,
    resetGroupDataBy,
    resetSelectedGroupings,
    getEmbed,
    cloneEmbedById,
    previewEmbed,
    createEmbed,
    createV3Embed,
    createV4Embed,
    unsetIsCreatingEmbed: noop
  };
}

export function AddTileContainer(props: ContainerProps) {
  const {
    isTemplate,
    entityId,
    entitySubdomain,
    story,
    storyContents,
    settings,
    messages,
    storyContent,
    selectedEmbed,
    isReportEmbedListSelected,
    onClose,
    publicationId,
    isAdmin
  } = props;

  const queryClient = useQueryClient();
  const sessionContext = useEmbedSession(entitySubdomain);
  const entity = props.entity ?? sessionContext.entity;
  const user = props.user ?? sessionContext.user;

  const { featureFlags } = useFeatureFlagStore();
  const canUseV3Embeds = featureFlags.get('product-feature-reporting-embed-v3') as boolean;
  const embedV2EditingEnabled = featureFlags.get('product-feature-reporting-embed-v2-editing-enabled') as boolean;
  const displayReportEmbeds = featureFlags.get('product-stories-update-report-embeds') as boolean;
  const accountSummaryChartsInEmbedEnabled = featureFlags.get(FLAG_PRODUCT_REPORTING_FEATURE_ACCOUNT_SUMMARY_CHARTS_IN_EMBEDS) as boolean;
  const useV3EmbedsForChartAndDetailsReports = featureFlags.get(FLAG_PRODUCT_REPORTING_CHART_AND_DETAILS_V3_ENABLED) as boolean;
  // Prefer prop (passed from outside a portal where the store context is unavailable)
  const useNewAddTileUI = props.useNewAddTileUI ?? featureFlags.get(FLAG_BNP_NEW_REPORT_TILE_MODAL) as boolean;
  const accountSummaryChartTitleAndDescriptionEnabled = featureFlags.get(FLAG_PRODUCT_REPORTING_FEATURE_CHARTS_CUSTOMIZATION_TITLE_AND_DESCRIPTION) as boolean;

  const hasGallery = useMemo(
    () => storyContents.some((sc) => sc.group === GALLERY),
    [storyContents]
  );

  const publicationQuery = usePublicationById(
    publicationId ? { publicationId } : null,
    messages.somethingWentWrong ?? ''
  );
  const publication = publicationQuery.data ?? undefined;

  const storyContentsQueryParams = useMemo(
    () => (entityId > 0 ? { storyId: story._id, entityId, isTemplate } : null),
    [story._id, entityId, isTemplate]
  );

  const storyQueryParams = useMemo(
    () => (entityId > 0 && !isTemplate ? { storyId: story._id, isTemplate } : null),
    [story._id, entityId, isTemplate]
  );

  const updateStoryContent = useCallback(
    (sc: IStoryContent) => {
      if (!storyContentsQueryParams) return;
      queryClient.setQueryData(
        storyViewQueryKeys.storyContents(storyContentsQueryParams),
        (old: IStoryContent[] | undefined) =>
          old?.map((c) => (c._id === sc._id ? sc : c)) ?? []
      );
    },
    [queryClient, storyContentsQueryParams]
  );

  const { mutate: updateStoryContentDB } = useUpdateStoryContentDB();

  const handleStoryEmbeds = useCallback(
    async (embedProps: any) => {
      const { story, displayReportEmbeds, storyEmbedUpdatePayload, isRemovingReportEmbed } = embedProps;

      const cachedContents = storyContentsQueryParams
        ? (queryClient.getQueryData(storyViewQueryKeys.storyContents(storyContentsQueryParams)) as IStoryContent[] | undefined) ?? []
        : [];

      const currentEmbeds: Array<{ id: string }> = [];
      cachedContents.forEach((sc) => {
        const fragment = document.createRange().createContextualFragment(sc.content);
        const mediaBlocks = Array.from(fragment.querySelectorAll('.media-block'));
        for (const mediaBlock of mediaBlocks) {
          for (let i = 0; i < mediaBlock.children.length; i++) {
            const child = mediaBlock.children[i];
            if (child.tagName === TAG_SCRIPT && child.hasAttribute('data-embed-id')) {
              const embedId = child.getAttribute('data-embed-id');
              if (embedId) currentEmbeds.push({ id: embedId });
            }
          }
        }
      });

      if (!displayReportEmbeds) {
        await removeEmbeds(story._id);
        await insertEmbeds(currentEmbeds.map((e) => ({ storyId: story._id, embedId: e.id })));
      } else {
        const variables = currentEmbeds.map((e) => {
          const payload: any = { storyId: story._id, embedId: e.id };
          if (storyEmbedUpdatePayload?.[e.id]) {
            const up = storyEmbedUpdatePayload[e.id];
            Object.assign(payload, {
              frequency: up.frequency,
              type: up.type || null,
              reportId: up.reportId || null,
              savedViewId: up.savedViewId || null,
              authorId: up.authorId,
              ...(up.isUpdating
                ? { updatedAt: new Date().toISOString(), createdAt: up.createdAt }
                : { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
            });
          }
          return payload;
        });
        if (!isRemovingReportEmbed) await insertEmbedsWithEmbedData(variables);
      }

      if (storyQueryParams) {
        queryClient.setQueryData<IStory>(storyViewQueryKeys.story(storyQueryParams), (prev) =>
          prev ? { ...prev, embeds: currentEmbeds.map((e) => e.id) } : prev
        );
      }
    },
    [queryClient, storyContentsQueryParams, storyQueryParams]
  );

  const deleteStoryEmbed = useCallback(
    async (embedProps: any) => {
      const { storyId, embedId, controller } = embedProps;
      const abortController = new AbortController();
      const response = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: deleteStoryEmbedQuery, variables: { storyId, embedId } })
        .signal(controller ?? abortController)
        .post()
        .json();
      if ((response as any).errors) return Promise.reject((response as any).errors);
      return response;
    },
    []
  );

  const { mutateAsync: deleteReportEmbeds } = useDeleteReportEmbeds(messages as any);
  const { toggleToast } = useToast();

  const getStoryContent = useCallback(
    async ({ storyId, entityId: eid, isTemplate: tmpl }: { storyId: string; entityId: number; isTemplate: boolean; entitySubdomain?: string }): Promise<IStoryContent[]> => {
      const params = storyContentsQueryParams && storyContentsQueryParams.storyId === storyId
        ? storyContentsQueryParams
        : { storyId, entityId: eid, isTemplate: tmpl };
      return queryClient.fetchQuery({
        queryKey: storyViewQueryKeys.storyContents(params),
        queryFn: async () => {
          const variables: Record<string, string | number> = {
            [tmpl ? 'storyTemplateId' : 'storyId']: storyId,
            limit: 100,
            ...(!tmpl && { entityId: eid })
          };
          const query = tmpl ? getTemplateContentsById : getStoryContentsById;
          const data = await postQuery(query, variables);
          const raw: IStoryContent[] = (data.storyContents ?? []).map(processStoryContentOrTemplateContent);
          return orderStoryContents(sortStoryContents(raw));
        }
      });
    },
    [queryClient, storyContentsQueryParams]
  );

  const tileState = useAddTileState();

  if (!entity || !user) {
    return <CircularProgress size={32} />;
  }

  return (
    <AddTile
      isTemplate={isTemplate}
      entityId={entityId}
      entitySubdomain={entitySubdomain}
      story={story}
      storyContents={storyContents}
      settings={settings ?? undefined}
      messages={messages}
      storyContent={storyContent}
      selectedEmbed={selectedEmbed as unknown as IStoryEmbedsResponse}
      isReportEmbedListSelected={isReportEmbedListSelected}
      onClose={onClose}
      entity={entity}
      isAdmin={isAdmin}
      user={user}
      hasGallery={!!hasGallery}
      canUseV3Embeds={canUseV3Embeds}
      embedV2EditingEnabled={embedV2EditingEnabled}
      displayReportEmbeds={displayReportEmbeds}
      accountSummaryChartsInEmbedEnabled={accountSummaryChartsInEmbedEnabled}
      useV3EmbedsForChartAndDetailsReports={useV3EmbedsForChartAndDetailsReports}
      useNewAddTileUI={useNewAddTileUI}
      accountSummaryChartTitleAndDescriptionEnabled={accountSummaryChartTitleAndDescriptionEnabled}
      publication={publication}
      updateStoryContent={updateStoryContent}
      updateStoryContentDB={updateStoryContentDB}
      handleStoryEmbeds={handleStoryEmbeds}
      deleteStoryEmbed={deleteStoryEmbed as any}
      deleteReportEmbeds={deleteReportEmbeds}
      getStoryContent={getStoryContent}
      toggleToast={toggleToast}
      {...tileState}
    />
  );
}
